import {
  getObject,
  getLeoUuid,
  LeoUUID,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { FileAttributes, FileAttributesDTO } from "../types/fileAttributes";
/* eslint-disable import/export */
export abstract class GetPresignedUploadURLForSpocsRPC implements
  LeoRPC<GetPresignedUploadURLForSpocsRPC.Request, GetPresignedUploadURLForSpocsRPC.Response, GetPresignedUploadURLForSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedUploadURLForSpocsRPC.Request
  ): Promise<LeoRPCResult<GetPresignedUploadURLForSpocsRPC.Response, GetPresignedUploadURLForSpocsRPC.Errors.Errors>>;
}

export namespace GetPresignedUploadURLForSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileAttributes: FileAttributesDTO;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedUploadURLForSpocsRPCRequest: boolean = true;
  
    readonly fileAttributes: FileAttributes;
    readonly projectId: LeoUUID;
  
  
    constructor(
    fileAttributes: FileAttributes, 
    projectId: LeoUUID
    ) {
      this.fileAttributes = fileAttributes;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileAttributes = FileAttributes.fromDTO(getObject(dto, "fileAttributes", InvalidRequestFileAttributesError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        fileAttributes,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileAttributes: this.fileAttributes.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForSpocsRPC(fileAttributes='${this.fileAttributes}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedUploadURLForSpocsResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLForSpocs(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLForSpocsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileAttributesError extends GetPresignedUploadURLForSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileAttributesError";
  }
}

export class InvalidRequestProjectIdError extends GetPresignedUploadURLForSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedUploadURLForSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedUploadURLForSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends GetPresignedUploadURLForSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

